import {
  Box,
  Typography,
  Grid,
  Button,
  Fab,
  Card,
  CardContent,
  CardActions,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  useMediaQuery,
  CircularProgress,
  Snackbar,
  Alert,
  Container,
  Chip,
  Avatar,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import LockIcon from "@mui/icons-material/Lock";
import AddIcon from "@mui/icons-material/Add";
import UpgradeIcon from "@mui/icons-material/WorkspacePremium";
import BusinessIcon from "@mui/icons-material/Business";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import VisibilityIcon from "@mui/icons-material/Visibility";
import ManageAccountsIcon from "@mui/icons-material/ManageAccounts";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import { getUserData, requestID } from "../../../common/utils";
import { createProject, allProjects } from "../../../Services/BusinessOwner";
import { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import { useMutation, useQueryClient } from "react-query";
import { Link } from "react-router-dom";
import styles from "./BusinessOwnerDashboard.module.css";
import HiredApplicantsList from "./HiredApplicantsList";
import { IonContent, IonPage } from "@ionic/react";

type Project = {
  project_id: string;
  project_name: string;
  budget_cost: number | null;
  business_id: string | null;
  created_at: string;
  status: string;
  tags: string;
  jobRoles: {
    jobRoleID: string;
    jobTitle: string;
    candidates: {
      user_id: number;
      full_name: string;
      email_address: string;
      phone_number: string;
      location_city: string;
      location_country: string;
      total_years_experience: string;
      status: {
        employed: number;
        interviewed: number;
        screened: number;
      };
    }[];
  }[];
};

const premiumAlternative = true;

const Dashboard = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const user = getUserData();
  const [projects, setProjects] = useState<Project[]>([]);
  const [isPremium, setIsPremium] = useState(false);
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [projectName, setProjectName] = useState("");
  const [projectTags, setProjectTags] = useState("");
  const [budget, setBudget] = useState("");
  const [loading, setLoading] = useState(true);
  const [hiredModalOpen, setHiredModalOpen] = useState(false);
  const [selectedProjectForHired, setSelectedProjectForHired] =
    useState<Project | null>(null);

  const history = useHistory();
  const queryClient = useQueryClient();

  useEffect(() => {
    // Simulate fetch
    // setProjects(mockProjects)
    setIsPremium(user?.isPremium || premiumAlternative);
    fetchAllProjects();
  }, []);

  const calculateProjectMetrics = (project: Project) => {
    const allCandidates = project?.jobRoles?.flatMap((role) => role.candidates);
    return {
      applicants: allCandidates.length,
      screened: allCandidates?.filter((c) => Number(c.status.screened) === 1)
        .length,
      hired: allCandidates?.filter((c) => Number(c.status.employed) === 1)
        .length,
    };
  };

  const totalApplicants = projects?.reduce(
    (sum, p) => sum + calculateProjectMetrics(p).applicants,
    0
  );
  const totalScreened = projects?.reduce(
    (sum, p) => sum + calculateProjectMetrics(p).screened,
    0
  );
  const totalHired = projects?.reduce(
    (sum, p) => sum + calculateProjectMetrics(p).hired,
    0
  );

  const { mutate, isLoading } = useMutation({
    mutationFn: async () => {
      const formData = new FormData();
      formData.append("requestID", requestID());
      formData.append("name", projectName);
      formData.append("tags", projectTags);
      formData.append("budgetcosts", budget);
      // return await createProject(formData)
      const response = await createProject(formData);
      if (response.success) {
        const updatedProjects = await fetchAllProjects();
        setProjects(updatedProjects.response);
        return response;
      }
      throw new Error(response.message || "Failed to create project");
    },
    onSuccess: (data) => {
      setMessage(data.message);
      setProjectName("");
      setProjectTags("");
      setBudget("");
      setOpen(false);
      queryClient.invalidateQueries(["projects"]);
    },
    onError: (error: any) => {
      setMessage(error.message);
    },
  });

  const fetchAllProjects = async () => {
    try {
      const response = await allProjects();
      console.log("Fetched projects:", response.response);
      if (response.response) {
        setProjects(response.response);
      }
    } catch (error) {
      console.error("Failed to fetch projects:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box
        className={styles.container}
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
      >
        <CircularProgress size={48} />
      </Box>
    );
  }

  return (
    <IonPage>
      <IonContent>
        <Box p={2} sx={{ height: "100%", margin: "auto" }} data-dashboard>
          <Box className={styles.header}>
            <Container className={styles.headerContent}>
              <Typography className={styles.welcomeText}>
                Welcome back, {user?.firstName || "Business Owner"}
              </Typography>
              <Typography className={styles.subtitle}>
                Manage your recruitment projects and track hiring progress
              </Typography>
            </Container>
          </Box>

          {/* Stats */}
          <Container className={styles.mainContent}>
            <Box className={styles.statsSection}>
              <Typography className={styles.sectionTitle}>
                <TrendingUpIcon />
                Recruitment Overview
              </Typography>
              <Grid container className={styles.statsGrid}>
                <Grid item xs={12} sm={4} md={3.9}>
                  <Box
                    className={styles.statCard}
                    style={{ "--stat-color": "#3b82f6" } as React.CSSProperties}
                  >
                    <Typography className={styles.statValue}>
                      {totalApplicants || 0}
                    </Typography>
                    <Typography className={styles.statLabel}>
                      Total Applicants
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={4} md={3.9}>
                  <Box
                    className={styles.statCard}
                    style={{ "--stat-color": "#f59e0b" } as React.CSSProperties}
                  >
                    <Typography className={styles.statValue}>
                      {totalScreened || 0}
                    </Typography>
                    <Typography className={styles.statLabel}>
                      Screened
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={4} md={3.9}>
                  <Box
                    className={styles.statCard}
                    style={{ "--stat-color": "#10b981" } as React.CSSProperties}
                  >
                    <Typography className={styles.statValue}>
                      {totalHired || 0}
                    </Typography>
                    <Typography className={styles.statLabel}>
                      Successfully Hired
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Box>

            {/* Upgrade CTA */}
            {!isPremium && (
              <Box className={styles.upgradeSection}>
                <Card className={styles.upgradeCard}>
                  <CardContent sx={{ textAlign: "center", p: 0 }}>
                    <UpgradeIcon sx={{ fontSize: 48, mb: 2, opacity: 0.9 }} />
                    <Typography variant="h5" fontWeight="bold" gutterBottom>
                      Unlock Premium Features
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 3, opacity: 0.9 }}>
                      Get full access to applicant details, advanced analytics,
                      and priority support
                    </Typography>
                    <Button
                      className={styles.upgradeButton}
                      size="large"
                      startIcon={<UpgradeIcon />}
                      onClick={() => history.push("/premium")}
                    >
                      Upgrade Now
                    </Button>
                  </CardContent>
                </Card>
              </Box>
            )}

            {/* Projects */}
            <Box className={styles.projectsSection}>
              <Typography className={styles.sectionTitle}>
                <BusinessIcon />
                Active Projects ({projects.length})
              </Typography>
              <Grid container spacing={3}>
                {projects.map((project) => (
                  <Grid item xs={12} lg={6} key={project.project_id}>
                    <ProjectCard
                      project={project}
                      isPremium={isPremium}
                      setHiredModalOpen={setHiredModalOpen}
                      setSelectedProjectForHired={setSelectedProjectForHired}
                    />
                  </Grid>
                ))}
              </Grid>
            </Box>
          </Container>

          <Fab
            color="primary"
            onClick={() => setOpen(true)}
            sx={{ position: "fixed", bottom: 88, right: 16 }}
            className={`${styles.fab} ${styles.addFab}`}
          >
            <AddIcon />
          </Fab>

          {/* WhatsApp Request */}
          <Fab
            color="success"
            sx={{ position: "fixed", bottom: 16, right: 16, zIndex: 1300 }}
            href={`https://wa.me/2349025697028?text=${encodeURIComponent(
              "Hello, I’d like to request recruitment help for my business."
            )}`}
            target="_blank"
          >
            <WhatsAppIcon />
          </Fab>

          {/* Modal */}
          <Dialog
            open={open}
            onClose={() => setOpen(false)}
            fullWidth
            maxWidth="sm"
            className={styles.modal}
            PaperProps={{
              className: styles.modalContent,
            }}
          >
            <DialogTitle className={styles.modalTitle}>
              Create New Project
            </DialogTitle>
            <DialogContent className={styles.modalBody}>
              <TextField
                label="Project Name"
                fullWidth
                value={projectName}
                onChange={(e) => setProjectName(e.target.value)}
                className={styles.textField}
                variant="outlined"
              />
              <TextField
                label="Skills & Requirements"
                fullWidth
                value={projectTags}
                onChange={(e) => setProjectTags(e.target.value)}
                className={styles.textField}
                variant="outlined"
                placeholder="e.g., Customer Service, Sales, Management"
              />
              <TextField
                label="Budget Range"
                fullWidth
                value={budget}
                onChange={(e) => setBudget(e.target.value)}
                className={styles.textField}
                variant="outlined"
                placeholder="e.g., $50,000 - $75,000"
              />
            </DialogContent>
            <DialogActions className={styles.modalActions}>
              <Button
                onClick={() => setOpen(false)}
                className={styles.cancelButton}
              >
                Cancel
              </Button>
              <Button
                onClick={() => mutate()}
                disabled={isLoading}
                variant="contained"
                className={styles.submitButton}
              >
                {isLoading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  "Create Project"
                )}
              </Button>
            </DialogActions>
          </Dialog>
          <Snackbar
            open={!!message}
            autoHideDuration={6000}
            onClose={() => setMessage("")}
            anchorOrigin={{ vertical: "top", horizontal: "center" }}
          >
            <Alert
              onClose={() => setMessage("")}
              severity={message.includes("success") ? "success" : "error"}
              sx={{ width: "100%" }}
            >
              {message}
            </Alert>
          </Snackbar>
          {selectedProjectForHired && (
            <HiredApplicantsList
              open={hiredModalOpen}
              onClose={() => {
                setHiredModalOpen(false);
                setSelectedProjectForHired(null);
              }}
              project={selectedProjectForHired}
              hiredCandidates={selectedProjectForHired.jobRoles.flatMap(
                (role) =>
                  role.candidates
                    .filter(
                      (candidate) => Number(candidate.status.employed) === 1
                    )
                    .map((candidate) => ({
                      ...candidate,
                      jobTitle: role.jobTitle,
                      jobRoleID: role.jobRoleID,
                    }))
              )}
            />
          )}
        </Box>
      </IonContent>
    </IonPage>
  );
};

export default Dashboard;

const ProjectCard = ({
  project,
  isPremium,
  setHiredModalOpen,
  setSelectedProjectForHired,
}: {
  project: Project;
  isPremium: boolean;
  setHiredModalOpen: (open: boolean) => void;
  setSelectedProjectForHired: (project: Project | null) => void;
}) => {
  const history = useHistory();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const user = getUserData();

  const projectMetrics = {
    applicants: project.jobRoles.flatMap((role) => role.candidates).length,
    screened: project.jobRoles
      .flatMap((role) => role.candidates)
      .filter((c) => Number(c.status.screened) === 1).length,
    hired: project.jobRoles
      .flatMap((role) => role.candidates)
      .filter((c) => Number(c.status.employed) === 1).length,
  };

  const handleHiredClick = () => {
    if (projectMetrics.hired > 0 && isPremium) {
      setSelectedProjectForHired(project);
      console.log("Hired project selected:", project);
      setHiredModalOpen(true);
    }
  };

  const jobRoleBreakdown = project.jobRoles
    .map((role) => ({
      title: role.jobTitle || "Untitled Role",
      total: role.candidates.length,
      hired: role.candidates.filter((c) => c.status.employed === 1).length,
    }))
    .filter((role) => role.total > 0);

  return (
    <Card className={styles.projectCard}>
      <Box className={styles.projectHeader}>
        <Typography className={styles.projectTitle}>
          {project.project_name}
        </Typography>
        <Box className={styles.projectHr}>
          <Avatar sx={{ width: 24, height: 24, bgcolor: "#667eea" }}>
            <BusinessIcon sx={{ fontSize: 14 }} />
          </Avatar>
          <Typography variant="body2">{project.status}</Typography>
        </Box>
      </Box>

      <CardContent className={styles.projectContent}>
        <Box className={styles.projectMetrics}>
          <Typography
            variant="body2"
            color="text.secondary"
            gutterBottom
            sx={{ fontWeight: 600, mb: 2 }}
          >
            Project Performance
          </Typography>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={4}>
              <Box
                className={styles.projectStatCard}
                style={{ "--stat-color": "#3b82f6" } as React.CSSProperties}
              >
                <Typography className={styles.projectStatValue}>
                  {projectMetrics.applicants || 0}
                </Typography>
                <Typography className={styles.projectStatLabel}>
                  Applicants
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={4}>
              <Box
                className={styles.projectStatCard}
                style={{ "--stat-color": "#f59e0b" } as React.CSSProperties}
              >
                <Typography className={styles.projectStatValue}>
                  {projectMetrics.screened || 0}
                </Typography>
                <Typography className={styles.projectStatLabel}>
                  Screened
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={4}>
              <Box
                className={`${styles.projectStatCard} ${
                  projectMetrics.hired > 0 && isPremium
                    ? styles.clickableStatCard
                    : ""
                }`}
                style={{ "--stat-color": "#10b981" } as React.CSSProperties}
                onClick={handleHiredClick}
                sx={{
                  cursor:
                    projectMetrics.hired > 0 && isPremium
                      ? "pointer"
                      : "default",
                  transition: "all 0.3s ease",
                  "&:hover":
                    projectMetrics.hired > 0 && isPremium
                      ? {
                          transform: "translateY(-2px)",
                          boxShadow: "0 8px 25px rgba(16, 185, 129, 0.3)",
                        }
                      : {},
                }}
              >
                <Typography className={styles.projectStatValue}>
                  {projectMetrics.hired || 0}
                </Typography>
                <Typography className={styles.projectStatLabel}>
                  {projectMetrics.hired > 0 && isPremium
                    ? "Hired (Click to Rate)"
                    : "Hired"}
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Box>

        {(user.isPremium || premiumAlternative) && (
          <Box sx={{ mb: 2 }}>
            <Typography
              variant="body2"
              color="text.secondary"
              gutterBottom
              sx={{ fontWeight: 600 }}
            >
              Job Roles Breakdown
            </Typography>
            {jobRoleBreakdown.length > 0 ? (
              jobRoleBreakdown.map((role, i) => (
                <Box key={i} className={styles.positionItem}>
                  <Typography className={styles.positionTitle}>
                    {role.title}
                  </Typography>
                  <Box className={styles.positionStats}>
                    <Chip
                      label={`${role.hired}/${role.total}`}
                      size="small"
                      color={role.hired === role.total ? "success" : "default"}
                      icon={<CheckCircleIcon />}
                    />
                  </Box>
                </Box>
              ))
            ) : (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ fontStyle: "italic" }}
              >
                No candidates yet
              </Typography>
            )}
          </Box>
        )}
      </CardContent>
      {/* <CardActions
        sx={{
          flexDirection: isMobile ? "column" : "row",
          justifyContent: "space-between"
        }}
      >
        <Button
          fullWidth
          variant="contained"
          onClick={() => history.push(`/folder/projects/${project.id}`)}
          disabled={!isPremium}
          startIcon={!isPremium ? <LockIcon /> : undefined}
          sx={{ marginBottom: isMobile ? 1 : 0 }}
        > */}
      <CardActions className={styles.projectActions}>
        <Button
          className={`${styles.actionButton} ${
            isPremium ? styles.primaryButton : styles.lockedButton
          }`}
          fullWidth={isMobile}
          onClick={() => history.push(`/folder/projects/${project.project_id}`)}
          component={Link}
          to={
            {
              pathname: "/folder/projects",
              search: `?id=${project.project_id}`,
              state: { project },
            } as any
          }
          disabled={!isPremium}
          startIcon={!isPremium ? <LockIcon /> : <VisibilityIcon />}
        >
          {isPremium ? "View Applicants" : "Upgrade to View"}
        </Button>
        {isPremium && (
          <Button
            className={`${styles.actionButton} ${styles.secondaryButton}`}
            fullWidth={isMobile}
            startIcon={<ManageAccountsIcon />}
            component={Link}
            to={
              {
                pathname: "/folder/jobprojectdetail",
                search: `?id=${project.project_id}`,
                state: { project },
              } as any
            }
          >
            Manage Project
          </Button>
        )}
      </CardActions>
    </Card>
  );
};
